import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Navbar = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);
  
  return (
    <Nav>
      <NavContainer>
        <Logo to="/">
          <LogoImage src="/logo.png" alt="Govind Industries" />
          <LogoText>
            GOVIND <span>INDUSTRIES</span>
          </LogoText>
        </Logo>
        
        <DesktopNavLinks>
          <NavLink to="/" $active={location.pathname === '/'}>Home</NavLink>
          <NavLink to="/about" $active={location.pathname === '/about'}>About</NavLink>
          <NavLink to="/products" $active={location.pathname === '/products'}>Products</NavLink>
          <NavLink to="/services" $active={location.pathname === '/services'}>Services</NavLink>
          <NavLink to="/contact" $active={location.pathname === '/contact'}>Contact</NavLink>
        </DesktopNavLinks>

        <MobileMenuButton 
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
          $isOpen={isMenuOpen}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuButton>
      </NavContainer>

      <MobileNavOverlay $isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
      
      <MobileNavLinks $isOpen={isMenuOpen}>
        <MobileNavLink to="/" $active={location.pathname === '/'}>
          Home
        </MobileNavLink>
        <MobileNavLink to="/about" $active={location.pathname === '/about'}>
          About
        </MobileNavLink>
        <MobileNavLink to="/products" $active={location.pathname === '/products'}>
          Products
        </MobileNavLink>
        <MobileNavLink to="/services" $active={location.pathname === '/services'}>
          Services
        </MobileNavLink>
        <MobileNavLink to="/contact" $active={location.pathname === '/contact'}>
          Contact
        </MobileNavLink>
      </MobileNavLinks>
    </Nav>
  );
};

// --- STYLED COMPONENTS ---
const Nav = styled.nav`
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 5%;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 15px 4%;
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 12px 3%;
    gap: 0.75rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  text-decoration: none;
  z-index: 1001;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    gap: 0.6rem;
    max-width: calc(100% - 60px);
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
    max-width: calc(100% - 50px);
  }
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  
  @media (max-width: 768px) {
    height: 50px;
  }
  
  @media (max-width: 480px) {
    height: 45px;
  }
`;

const LogoText = styled.div`
  font-size: 1.5rem;
  font-weight: 900;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 400px) {
    font-size: 1rem;
  }
  
  @media (max-width: 350px) {
    font-size: 0.9rem;
  }
`;

const DesktopNavLinks = styled.div`
  display: flex;
  gap: 25px;
  // font-weight: 600;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.$active ? props.theme.colors.primary : '#333'};
  text-decoration: none;
  transition: color 0.3s ease;
  padding: 0.5rem 0;
  position: relative;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: ${props => props.$active ? '100%' : '0'};
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;
  flex-shrink: 0;
  
  span {
    display: block;
    height: 3px;
    width: 25px;
    background-color: #333;
    margin: 3px 0;
    transition: all 0.3s ease;
    transform-origin: center;
  }
  
  ${props => props.$isOpen && `
    span:nth-child(1) {
      transform: rotate(45deg) translate(6px, 6px);
    }
    
    span:nth-child(2) {
      opacity: 0;
    }
    
    span:nth-child(3) {
      transform: rotate(-45deg) translate(6px, -6px);
    }
  `}
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileNavOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 998;
`;

const MobileNavLinks = styled.div`
  position: fixed;
  top: 0;
  right: ${props => props.$isOpen ? '0' : '-100%'};
  width: 280px;
  height: 100vh;
  background: white;
  padding: 80px 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
  transition: right 0.3s ease;
  z-index: 999;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 480px) {
    width: 250px;
    padding: 70px 1.5rem 1.5rem;
  }
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.$active ? props.theme.colors.primary : '#333'};
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 1rem 0;
  border-bottom: 1px solid #eee;
  transition: color 0.3s ease, padding-left 0.3s ease;
  position: relative;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    padding-left: 1rem;
  }
  
  ${props => props.$active && `
    color: ${props.theme.colors.primary};
    
    &:before {
      content: '';
      position: absolute;
      left: -1rem;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background-color: ${props.theme.colors.primary};
      border-radius: 2px;
    }
  `}
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.875rem 0;
  }
`;

export default Navbar;